import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import './Beverages.css';

// Add toast notifications
const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  // Create a simple toast notification
  const toast = document.createElement('div');
  toast.className = `toast-notification ${type}`;
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 12px 20px;
    border-radius: 8px;
    color: white;
    font-weight: 500;
    z-index: 9999;
    animation: slideIn 0.3s ease;
    ${type === 'success' ? 'background: #198754;' : 'background: #dc3545;'}
  `;
  
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.style.animation = 'slideOut 0.3s ease';
    setTimeout(() => document.body.removeChild(toast), 300);
  }, 3000);
};

// Add CSS for toast animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(style);

interface Beverage {
  id: number;
  name: string;
  description?: string;
  price: number;
  category: string;
  is_veg: boolean;
  image?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Beverages: React.FC = () => {
  const [beverages, setBeverages] = useState<Beverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBeverage, setEditingBeverage] = useState<Beverage | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'beverages',
    is_veg: true,
    image: '',
    is_active: true
  });

  useEffect(() => {
    fetchBeverages();
  }, []);

  const fetchBeverages = async () => {
    try {
      setLoading(true);
      const response = await adminService.getBeverages();
      if (response.success) {
        setBeverages(response.data);
      } else {
        console.error('Failed to fetch beverages:', response.error);
      }
    } catch (error) {
      console.error('Error fetching beverages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.name.trim()) {
      showToast('Please enter a beverage name', 'error');
      return;
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      showToast('Please enter a valid price', 'error');
      return;
    }
    
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        category: formData.category,
        is_veg: formData.is_veg,
        image: formData.image.trim() || null,
        is_active: formData.is_active
      };

      console.log('Submitting beverage payload:', payload);
      
      let response;
      if (editingBeverage) {
        response = await adminService.updateBeverage(editingBeverage.id, payload);
      } else {
        response = await adminService.createBeverage(payload);
      }

      console.log('API response:', response);

      if (response && response.success) {
        showToast(
          editingBeverage ? 'Beverage updated successfully!' : 'Beverage created successfully!',
          'success'
        );
        await fetchBeverages();
        setShowModal(false);
        setEditingBeverage(null);
        setFormData({
          name: '',
          description: '',
          price: '',
          category: 'beverages',
          is_veg: true,
          image: '',
          is_active: true
        });
      } else {
        const errorMessage = response?.error || 'Failed to save beverage';
        console.error('API error:', errorMessage);
        showToast(errorMessage, 'error');
      }
    } catch (error: any) {
      console.error('Error saving beverage:', error);
      const errorMessage = error?.message || 'Network error occurred';
      showToast(errorMessage, 'error');
    }
  };

  const handleEdit = (beverage: Beverage) => {
    setEditingBeverage(beverage);
    setFormData({
      name: beverage.name,
      description: beverage.description || '',
      price: beverage.price.toString(),
      category: beverage.category,
      is_veg: beverage.is_veg,
      image: beverage.image || '',
      is_active: beverage.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this beverage?')) {
      return;
    }

    try {
      const response = await adminService.deleteBeverage(id);
      if (response && response.success) {
        showToast('Beverage deleted successfully!', 'success');
        await fetchBeverages();
      } else {
        const errorMessage = response?.error || 'Failed to delete beverage';
        console.error('API error:', errorMessage);
        showToast(errorMessage, 'error');
      }
    } catch (error: any) {
      console.error('Error deleting beverage:', error);
      const errorMessage = error?.message || 'Network error occurred';
      showToast(errorMessage, 'error');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      const response = await adminService.toggleBeverageStatus(id);
      if (response && response.success) {
        showToast('Beverage status updated successfully!', 'success');
        await fetchBeverages();
      } else {
        const errorMessage = response?.error || 'Failed to toggle beverage status';
        console.error('API error:', errorMessage);
        showToast(errorMessage, 'error');
      }
    } catch (error: any) {
      console.error('Error toggling beverage status:', error);
      const errorMessage = error?.message || 'Network error occurred';
      showToast(errorMessage, 'error');
    }
  };

  const resetForm = () => {
    setEditingBeverage(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'beverages',
      is_veg: true,
      image: '',
      is_active: true
    });
  };

  // Filter beverages based on search and filters
  const filteredBeverages = beverages.filter(beverage => {
    const matchesSearch = beverage.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (beverage.description && beverage.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || beverage.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && beverage.is_active) ||
                         (statusFilter === 'inactive' && !beverage.is_active);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <h2>Beverages Management</h2>
        <button 
          className="btn-add-beverage"
          onClick={() => setShowModal(true)}
        >
          <span>+</span> Add Beverage
        </button>
      </div>

      {/* Stats Cards */}
      <div className="beverages-stats">
        <div className="stat-card total">
          <div className="stat-number">{beverages.length}</div>
          <div className="stat-label">Total Beverages</div>
        </div>
        <div className="stat-card active">
          <div className="stat-number">{beverages.filter(b => b.is_active).length}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card inactive">
          <div className="stat-number">{beverages.filter(b => !b.is_active).length}</div>
          <div className="stat-label">Inactive</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search beverages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="form-control-custom filter-dropdown"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="beverages">Beverages</option>
            <option value="juices">Juices</option>
            <option value="snacks">Snacks</option>
            <option value="combos">Combos</option>
          </select>
          <select 
            className="form-control-custom filter-dropdown"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="beverages-table">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-custom"></div>
            <span>Loading beverages...</span>
          </div>
        ) : filteredBeverages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🥤</div>
            <h4>No beverages found</h4>
            <p>
              {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by adding your first beverage'}
            </p>
            {!searchTerm && categoryFilter === 'all' && statusFilter === 'all' && (
              <button 
                className="btn-add-beverage"
                onClick={() => setShowModal(true)}
              >
                <span>+</span> Add First Beverage
              </button>
            )}
          </div>
        ) : (
          <table className="table table-custom">
            <thead>
              <tr>
                <th>Beverage</th>
                <th>Category</th>
                <th>Price</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBeverages.map((beverage) => (
                <tr key={beverage.id} className={beverage.is_active ? 'offer-active' : ''}>
                  <td>
                    <div className="beverage-info">
                      <div className="beverage-image">
                        {beverage.image ? (
                          <img src={beverage.image} alt={beverage.name} />
                        ) : (
                          <span>🥤</span>
                        )}
                      </div>
                      <div className="beverage-details">
                        <h6>{beverage.name}</h6>
                        <p>{beverage.description}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`category-badge ${beverage.category}`}>
                      {beverage.category}
                    </span>
                  </td>
                  <td>
                    <div className="price-display">{beverage.price}</div>
                  </td>
                  <td>
                    <span className={`badge ${beverage.is_veg ? 'bg-success' : 'bg-danger'}`}>
                      {beverage.is_veg ? 'Veg' : 'Non-Veg'}
                    </span>
                  </td>
                  <td>
                    <label className="status-toggle">
                      <input
                        type="checkbox"
                        checked={beverage.is_active}
                        onChange={() => handleToggleStatus(beverage.id)}
                      />
                      <span className="status-slider"></span>
                    </label>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-action edit"
                        onClick={() => handleEdit(beverage)}
                      >
                        <span>✏️</span> Edit
                      </button>
                      <button
                        className="btn-action delete"
                        onClick={() => handleDelete(beverage.id)}
                      >
                        <span>🗑️</span> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header-custom">
                <h5 className="modal-title">
                  {editingBeverage ? 'Edit Beverage' : 'Add New Beverage'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBeverage(null);
                  }}
                ></button>
              </div>
              <div className="form-section">
                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label required">Name</label>
                      <input
                        type="text"
                        className="form-control-custom"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <select
                        className="form-control-custom"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                      >
                        <option value="beverages">Beverages</option>
                        <option value="juices">Juices</option>
                        <option value="snacks">Snacks</option>
                        <option value="combos">Combos</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label required">Price</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control-custom"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Image URL</label>
                      <input
                        type="url"
                        className="form-control-custom"
                        value={formData.image}
                        onChange={(e) => setFormData({...formData, image: e.target.value})}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control-custom"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      placeholder="Enter beverage description..."
                    />
                  </div>
                  <div className="form-group">
                    <div className="form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isVeg"
                        checked={formData.is_veg}
                        onChange={(e) => setFormData({...formData, is_veg: e.target.checked})}
                      />
                      <label className="form-label" htmlFor="isVeg">
                        Vegetarian
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      />
                      <label className="form-label" htmlFor="isActive">
                        Active
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer-custom">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setShowModal(false);
                        setEditingBeverage(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-save"
                    >
                      {editingBeverage ? 'Update Beverage' : 'Create Beverage'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Beverages;
